import React, { useState, useEffect } from 'react';
import { updateAdminConfig, getPriceHistory } from '../../utils/api';

const SystemStatus = ({ config, loadConfig, flash, f, BC, SYM, bcRate, toBC, fBC, vBC, isNotUsdt, showUsdt }) => {
  const [editTokenName, setEditTokenName] = useState(config.tokenName);
  const [editTokenSymbol, setEditTokenSymbol] = useState(config.tokenSymbol);
  const [priceHistory, setPriceHistory] = useState([]);
  const [chartRange, setChartRange] = useState('30');

  const tpBC = toBC(config.tokenPriceInUsdt);
  const oneBC_tokens = config.tokenPriceInUsdt > 0 ? (bcRate / config.tokenPriceInUsdt) : 0;

  useEffect(() => {
    getPriceHistory(30).then(r => setPriceHistory(r.points || [])).catch(() => {});
  }, []);

  const handleSave = async () => {
    try { await updateAdminConfig({ tokenName: editTokenName, tokenSymbol: editTokenSymbol }); flash('Token info updated'); loadConfig(); }
    catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
  };

  return (
    <div className="fade-in">
      <h2 className="adm-title">System Dashboard</h2>
      <div className="adm-kpi-grid">
        {[
          ['Token', `${config.tokenName} (${SYM})`],
          ['Price', BC === 'USDT' ? `${f(config.tokenPriceInUsdt, 8)} USDT` : `${f(tpBC, 10)} ${BC}${showUsdt ? ` ≈ ${f(config.tokenPriceInUsdt, 6)} USDT` : ''}`],
          [`1 ${BC} =`, `${f(oneBC_tokens, 2)} ${SYM}`],
          ['Total Supply', `${f(config.totalSupply, 2)} ${SYM}`],
          ['Tokens Burned', `${f(config.totalBurned, 2)} ${SYM}`],
          ['Liquidity (70%)', vBC(config.liquidity)],
          ['Collection', vBC(config.totalCollection)],
          ['Team Income (15%)', vBC(config.levelFund)],
          ['Royalty (5%)', vBC(config.royaltyFund)],
          ['Admin (10%)', vBC(config.adminPool || 0)],
          ['Burn in Liq (10%)', vBC(config.buyBurnPool || 0)],
          ['Sell Fee Pool', vBC(config.sellBurnPool || 0)],
          ['Undistributed', vBC(config.adminRetainedFund)],
          ['Users', config.totalUsers],
          ['Buys', config.totalBuys],
          ['Sells', config.totalSells || 0],
          ['Buy', config.isBuyPaused ? '⏸ Paused' : '▶ Active'],
          ['Sell', config.isSellPaused ? '⏸ Paused' : '▶ Active'],
        ].map(([label, val]) => (
          <div key={label} className="adm-kpi card"><span className="adm-kpi-label">{label}</span><strong className="adm-kpi-val mono">{val}</strong></div>
        ))}
      </div>

      {/* Allocation Breakdown */}
      <div className="card adm-sub">
        <h3>Investment Allocation Breakdown</h3>
        <p className="adm-hint">How each purchase splits. Verified: 70 + 15 + 5 + 10 = 100%</p>
        <div className="table-wrap"><table className="table">
          <thead><tr><th>Pool</th><th>%</th><th>What happens</th><th>Accumulated</th></tr></thead>
          <tbody>
            <tr><td>Liquidity</td><td>70%</td><td>Token backing (60% user + 10% burn backing)</td><td className="mono">{vBC(config.liquidity)}</td></tr>
            <tr><td>Team Income</td><td>15%</td><td>Distributed to upline levels 1-10</td><td className="mono">{vBC(config.levelFund)} (claimable) + {vBC(config.adminRetainedFund)} (undist.)</td></tr>
            <tr><td>Royalty</td><td>5%</td><td>Royalty pool</td><td className="mono">{vBC(config.royaltyFund)}</td></tr>
            <tr><td>Admin</td><td>10%</td><td>Admin operational fund</td><td className="mono">{vBC(config.adminPool || 0)}</td></tr>
            <tr className="table-row-highlight"><td><strong>Total</strong></td><td><strong>100%</strong></td><td></td><td className="mono"><strong>{vBC(config.totalCollection)}</strong></td></tr>
          </tbody>
        </table></div>
      </div>

      {/* Price Chart */}
      {priceHistory.length > 1 && (
        <div className="card adm-sub">
          <div className="d-chart-header">
            <h3 className="d-chart-title">{SYM} Price Chart</h3>
            <div className="d-chart-range">
              {[['1','1D'],['7','7D'],['30','1M'],['90','3M'],['365','1Y'],['9999','ALL']].map(([d,l]) => (
                <button key={d} className={`d-range-btn ${chartRange===d?'active':''}`}
                  onClick={async()=>{setChartRange(d);try{const r=await getPriceHistory(parseInt(d));setPriceHistory(r.points||[]);}catch{}}}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="d-chart-current">
            <span className="d-chart-price">{BC === 'USDT' ? f(config.tokenPriceInUsdt, 8) : f(tpBC, 10)} {BC}</span>
            {showUsdt && isNotUsdt && <span className="d-usdt-eq" style={{marginLeft:8}}>≈ {f(config.tokenPriceInUsdt, 6)} USDT</span>}
            {priceHistory.length>=2 && (()=>{const first=toBC(priceHistory[0]?.price||0);const last=toBC(priceHistory[priceHistory.length-1]?.price||0);const ch=first>0?((last-first)/first*100):0;return <span className={`d-chart-change ${ch>=0?'up':'down'}`}>{ch>=0?'+':''}{ch.toFixed(2)}%</span>;})()}
          </div>
          <div className="d-chart-wrap">
            <svg viewBox="0 0 900 220" className="d-chart-svg" preserveAspectRatio="xMidYMid meet">
              {(()=>{const pts=priceHistory.map(p=>({...p,price:toBC(p.price)}));const prices=pts.map(p=>p.price);const minP=Math.min(...prices);const maxP=Math.max(...prices);const pad=(maxP-minP)*0.05||minP*0.01;const yMin=minP-pad;const yMax=maxP+pad;const rng=yMax-yMin||1;const cW=780,cH=180,pL=5,pT=10;
              const pp=pts.map((p,i)=>{const x=pL+(i/Math.max(pts.length-1,1))*cW;const y=pT+cH-((p.price-yMin)/rng)*cH;return `${i===0?'M':'L'}${x.toFixed(1)},${y.toFixed(1)}`;}).join(' ');
              const fp=pts.map((p,i)=>{const x=pL+(i/Math.max(pts.length-1,1))*cW;const y=pT+cH-((p.price-yMin)/rng)*cH;return `${x.toFixed(1)},${y.toFixed(1)}`;});
              const fill=`M${pL},${pT+cH} ${fp.join(' L')} L${(pL+cW).toFixed(1)},${pT+cH} Z`;
              const lP=prices[prices.length-1];const isUp=lP>=prices[0];const col=isUp?'#00c853':'#ff1744';
              const lc=5;const yLbls=Array.from({length:lc},(_,i)=>yMin+(rng*i/(lc-1)));const md=Math.max(6,Math.ceil(-Math.log10(rng/lc))+2);
              return (<><defs><linearGradient id="admGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={col} stopOpacity="0.15"/><stop offset="100%" stopColor={col} stopOpacity="0.01"/></linearGradient></defs>
              {yLbls.map((v,i)=>{const y=pT+cH-((v-yMin)/rng)*cH;return <g key={i}><line x1={pL} y1={y} x2={pL+cW} y2={y} stroke="#e0e0e0" strokeWidth="0.5" strokeDasharray="4,4"/><text x={pL+cW+8} y={y+4} fill="#999" fontSize="10" fontFamily="monospace">{v.toFixed(md)}</text></g>;})}
              <path d={fill} fill="url(#admGrad)"/><path d={pp} fill="none" stroke={col} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              {pts.length>0&&(()=>{const x=pL+cW;const y=pT+cH-((lP-yMin)/rng)*cH;return <><circle cx={x} cy={y} r="5" fill={col}/><circle cx={x} cy={y} r="8" fill={col} opacity="0.2"/></>;})()}</>);})()}
            </svg>
          </div>
        </div>
      )}

      <div className="card adm-sub">
        <h3>Edit Token Info</h3>
        <div className="adm-form-grid cols-3">
          <div className="adm-field"><label>Token Name</label><input value={editTokenName} onChange={e=>setEditTokenName(e.target.value)} className="input"/></div>
          <div className="adm-field"><label>Token Symbol</label><input value={editTokenSymbol} onChange={e=>setEditTokenSymbol(e.target.value)} className="input"/></div>
          <div className="adm-field adm-field-end"><button className="btn btn-primary" onClick={handleSave}>Save</button></div>
        </div>
      </div>
    </div>
  );
};

export default SystemStatus;